import React from "react";

import { DeleteOutline } from "@mui/icons-material";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { formatPhoneNumber } from "@/common/utils";
import { ContactErrors } from "@/components/alerts/add-v2/model/contactRows";
import { BulletinContact } from "@/components/alerts/add-v2/model/types";

import { SPLIT_ROW_SX } from "./rowLayout";

/**
 * One point-of-contact row: a heading with the remove button, then Name / Badge #, Email, and
 * Phone / Ext.
 *
 * **The three-line layout is the prototype's**, built from its own `splitStyle` (`2fr 1fr`, one
 * column on a phone) — email gets a line of its own because an address is the widest thing in the
 * row. It is not the production `ContactInfoBox` layout, which puts email and phone on one line.
 *
 * `ContactInfoBox` is not reused for the reasons in README decision 7: it reads `SingleAlertContext`
 * for its errors, pushes a row into its own props during render, and owns the trailing-blank rule
 * that `model/contactRows.ts` now holds. What is reused is the piece worth reusing — `formatPhoneNumber`.
 *
 * The errors are `validateContactRow`'s, handed in by the section — the same call the schema makes,
 * so the count and the red field can never disagree. "Add an email or phone number" is a rule about
 * the pair, so it lands on the email and the phone field is left alone rather than both going red
 * for one missing answer.
 */

interface ContactRowProps {
  value: BulletinContact;
  /** 1-based, for the heading and the name label. Rows are numbered as displayed, never stored. */
  index: number;
  onChange: (next: BulletinContact) => void;
  onRemove: () => void;
  /** False for the trailing blank row, which self-manages — there is nothing there to remove. */
  removable: boolean;
  /** As the other rows: what is wrong, with the section deciding whether to say so yet. */
  errors: ContactErrors;
}

export const ContactRow: React.FC<ContactRowProps> = ({
  value,
  index,
  onChange,
  onRemove,
  removable,
  errors,
}) => {
  const { t } = useTranslation("bulletin/create-v2");

  const message = (code: string | undefined) =>
    code ? t(`validation.contact_${code}`) : undefined;

  const patch = (next: Partial<BulletinContact>) =>
    onChange({ ...value, ...next });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          color="text.secondary"
          sx={{
            fontSize: 12,
            textTransform: "uppercase",
            letterSpacing: ".4px",
          }}
        >
          {t("fields.contact_row", { index })}
        </Typography>
        <IconButton
          size="small"
          aria-label={t("fields.remove_contact")}
          disabled={!removable}
          onClick={onRemove}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={SPLIT_ROW_SX}>
        <TextField
          size="small"
          // Only the first row is required. Any other row that carries a detail needs a name too,
          // but that is a conditional rule, so it belongs to the schema rather than to a prop here.
          required={index === 1}
          error={!!errors.name}
          helperText={message(errors.name)}
          label={t("fields.contact_name", { index })}
          placeholder={t("fields.contact_name_placeholder")}
          value={value.name}
          onChange={(event) => patch({ name: event.target.value })}
        />
        <TextField
          size="small"
          label={t("fields.badge_number")}
          value={value.badgeNumber}
          onChange={(event) => patch({ badgeNumber: event.target.value })}
        />
      </Box>

      <TextField
        size="small"
        fullWidth
        error={!!errors.email}
        helperText={message(errors.email)}
        label={t("fields.email")}
        value={value.email}
        onChange={(event) => patch({ email: event.target.value })}
      />

      <Box sx={SPLIT_ROW_SX}>
        <TextField
          size="small"
          error={!!errors.phoneNumber}
          helperText={message(errors.phoneNumber)}
          label={t("fields.phone_number")}
          placeholder={t("fields.phone_number_placeholder")}
          value={value.phoneNumber}
          // Formats as you type, as production does. The digits-only form the payload wants is the
          // adapter's job, not this field's — production strips it in `saveBulletin`.
          onChange={(event) =>
            patch({ phoneNumber: formatPhoneNumber(event.target.value) })
          }
        />
        <TextField
          size="small"
          label={t("fields.extension")}
          value={value.extension}
          onChange={(event) => patch({ extension: event.target.value })}
        />
      </Box>
    </Box>
  );
};
